import { DEFAULT_GENERATE_HORIZON_DAYS, JOB_PRIORITIES } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Chip,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  TextInput,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { EmptyState } from '../../EmptyState.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { vesselTime } from '../personnel/ship-clock.js';
import { errorCode } from '../personnel/source.js';
import { utcToShipLocal } from './counter-form.js';
import { DefectsTab } from './DefectsTab.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import {
  activityOptions,
  MAINTENANCE_CATEGORIES,
  matchesActivity,
  matchesCategory,
} from './maintenance-filters.js';
import classes from './Pms.module.css';
import { WorkOrderPanel } from './PmsPanels.js';
import work from './PmsWork.module.css';
import { LoadFailed, usePmsQuery, useVesselChoice, VesselPicker } from './PmsWorkParts.js';
import type { PmsSource } from './source.js';
import { type WorkOrderStatusKey, workOrderStatus } from './status-tone.js';
import type {
  Assignee,
  EquipmentTree,
  JobView,
  OpenWorkOrderInput,
  PendingItem,
  WorkOrderList,
  WorkOrderView,
} from './types.js';
import { WorkOrderDetail } from './WorkOrderDetail.js';

export interface WorkOrdersScreenProps {
  source: PmsSource;
  now?: () => Date;
}

type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vesselId: string;
      list: WorkOrderList;
      jobs: JobView[];
      tree: EquipmentTree | null;
      pending: PendingItem[];
    };

const STATUS_FILTERS = ['all', 'open', 'pending', 'completed', 'returned', 'cancelled'] as const;
/** Sayfa içi sekmeler (ADR 0027 §12; plan M7 Task 21b): menü değişmez. */
const TABS = ['orders', 'defects', 'breakdowns'] as const;
type Tab = (typeof TABS)[number];
type StatusFilter = (typeof STATUS_FILTERS)[number];

/** Durum süzgeci: "Open" planlı ve süren iş emirleri. */
export function matchesStatus(wo: WorkOrderView, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  const status: WorkOrderStatusKey = workOrderStatus(wo);
  if (filter === 'open') return status === 'planned' || status === 'in_progress';
  return status === filter;
}

const systemNow = () => new Date();

/**
 * Maintenance (PMS) ▸ Work Orders (`/pms/work-orders`; plan M7 Task 14b): referansın "Work
 * Order" paneli büyütülmüş; arama, durum süzgeci, sıralama ve sayfalama. Gemide "+ New Work
 * Order" ve "Generate Work Orders" (K9, K13). Detay aynı adreste `?wo=<id>`; Quick Links "Create
 * Work Order" `?new=1` açar. Ofiste salt okunur.
 */
export function WorkOrdersScreen({ source, now = systemNow }: WorkOrdersScreenProps) {
  const { t } = useTranslation();
  const [query, setQuery] = usePmsQuery();
  const choice = useVesselChoice(source, query.get('vessel'));
  const vesselId = choice.vesselId;
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [crumb, setCrumb] = useState<string | null>(null);
  const woId = query.get('wo');
  const creating = query.get('new') === '1';
  // Equipment List "Generate Work Orders" `?generate=1` ile gelir
  const generateParam = query.get('generate') === '1';
  const filter = (STATUS_FILTERS as readonly string[]).includes(query.get('status') ?? '')
    ? (query.get('status') as StatusFilter)
    : 'all';
  const tab: Tab =
    source.defects && (TABS as readonly string[]).includes(query.get('tab') ?? '')
      ? (query.get('tab') as Tab)
      : 'orders';

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!vesselId) return;
    let live = true;
    setData((current) =>
      current.kind === 'ready' && current.vesselId === vesselId ? current : { kind: 'loading' },
    );
    Promise.all([
      source.workOrders ? source.workOrders(vesselId) : Promise.resolve({ items: [] }),
      source.jobs ? source.jobs(vesselId).then((list) => list.items) : Promise.resolve([]),
      source.tree(vesselId).catch(() => null),
      source.pending ? source.pending().catch(() => []) : Promise.resolve([]),
    ])
      .then(([list, jobs, tree, pending]) => {
        if (live) setData({ kind: 'ready', vesselId, list, jobs, tree, pending });
      })
      .catch((error: unknown) => {
        if (live) setData({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);
  const ready = data.kind === 'ready' ? data : null;
  // Makine kalemi, kategori ve etkinlik süzgeçleri (DNV-CP-0206 Sec.2 [4.4] b; ADR 0133 §1)
  const equipmentFilter = query.get('equipment') ?? '';
  const categoryFilter = query.get('category') ?? '';
  const activityFilter = query.get('activity') ?? '';
  // UMS / E0 alarm testi iş emirleri (DNV-CP-0206 Sec.2 [4.3] d; ADR 0133 §3)
  const alarmOnly = query.get('alarm') === '1';
  const alarmJobs = useMemo(
    () => new Set((ready?.jobs ?? []).filter((j) => j.alarmTest === true).map((j) => j.id)),
    [ready],
  );
  const items = useMemo(
    () =>
      ready
        ? ready.list.items.filter(
            (wo) =>
              matchesStatus(wo, filter) &&
              (equipmentFilter === '' || wo.equipmentId === equipmentFilter) &&
              matchesCategory(wo.kind, categoryFilter) &&
              matchesActivity(wo.workType, activityFilter) &&
              (!alarmOnly || (wo.jobId !== null && alarmJobs.has(wo.jobId))),
          )
        : null,
    [ready, filter, equipmentFilter, categoryFilter, activityFilter, alarmOnly, alarmJobs],
  );
  const equipmentOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const wo of ready?.list.items ?? []) {
      if (!seen.has(wo.equipmentId)) {
        seen.set(
          wo.equipmentId,
          [wo.equipmentCode, wo.equipmentName].filter(Boolean).join(' · ') || wo.equipmentId,
        );
      }
    }
    return [...seen]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [ready]);
  const activities = useMemo(
    () => activityOptions((ready?.list.items ?? []).map((wo) => wo.workType)),
    [ready],
  );
  const canCreate =
    source.scope === 'ship' && Boolean(source.work) && (ready?.list.can?.create ?? false);
  const timeZone = vesselTime(choice.vessel);
  const title = t('menu.pmsWorkOrders');

  if (choice.vessels !== null && choice.vessels.length === 0) {
    return (
      <div className={classes.screen}>
        <PageHeader title={title} />
        <Panel>
          <EmptyState title={t('pms.equipment.noVessel')} />
        </Panel>
      </div>
    );
  }
  const failed = choice.failed ?? (data.kind === 'error' ? data.code : null);

  return (
    <div className={classes.screen} data-pms-screen>
      <PageHeader
        title={title}
        {...(woId && crumb ? { crumb } : {})}
        {...(woId ? {} : { description: t('pms.work.workOrders.description') })}
        actions={
          <span className={work.headerActions}>
            {woId ? (
              <Button
                variant="default"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => {
                  setCrumb(null);
                  setQuery({ wo: null });
                }}
              >
                {t('pms.work.detail.back')}
              </Button>
            ) : (
              <VesselPicker
                scope={source.scope}
                vessels={choice.vessels}
                value={vesselId}
                onChange={(id) =>
                  setQuery({
                    vessel: id,
                    wo: null,
                    status: null,
                    new: null,
                    defect: null,
                    decide: null,
                  })
                }
              />
            )}
          </span>
        }
      />
      {notice ? (
        <Alert
          color="green"
          withCloseButton
          closeButtonLabel={t('pms.work.detail.dismiss')}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      ) : null}
      {failed ? (
        <Panel>
          <LoadFailed
            code={failed}
            onRetry={() => {
              choice.retry();
              reload();
            }}
          />
        </Panel>
      ) : woId && vesselId ? (
        <WorkOrderDetail
          source={source}
          vesselId={vesselId}
          timeZone={timeZone}
          woId={woId}
          jobs={ready?.jobs ?? []}
          pending={ready?.pending ?? []}
          me={source.userId ? { id: source.userId, name: source.userName ?? '' } : null}
          now={now}
          onChanged={reload}
          onLoaded={(wo) => setCrumb(wo.woNo)}
          {...(ready?.list.can ? { can: ready.list.can } : {})}
        />
      ) : (
        <Tabs
          value={tab}
          onChange={(value) =>
            setQuery({
              tab: value === 'orders' ? null : value,
              defect: null,
              decide: null,
              newDefect: null,
              dstatus: null,
            })
          }
          className={work.pageTabs}
          keepMounted={false}
        >
          {source.defects ? (
            <Tabs.List>
              {TABS.map((key) => (
                <Tabs.Tab key={key} value={key}>
                  {t(`pms.condition.defects.tabs.${key === 'orders' ? 'workOrders' : key}`)}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          ) : null}
          <Tabs.Panel value="orders">
            <div className={work.toolbar}>
              <SegmentedControl
                size="xs"
                aria-label={t('pms.work.workOrders.statusFilter')}
                value={filter}
                onChange={(value) => setQuery({ status: value === 'all' ? null : value })}
                data={STATUS_FILTERS.map((key) => ({
                  value: key,
                  label: t(`pms.work.workOrders.filters.${key}`),
                }))}
                className={work.statusFilter}
              />
              <Select
                size="xs"
                aria-label={t('pms.work.workOrders.equipmentFilter')}
                placeholder={t('pms.work.workOrders.equipmentFilter')}
                searchable
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                data={equipmentOptions}
                value={equipmentFilter || null}
                onChange={(value) => setQuery({ equipment: value })}
              />
              <Select
                size="xs"
                aria-label={t('pms.work.workOrders.categoryFilter')}
                placeholder={t('pms.work.workOrders.categoryFilter')}
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                data={MAINTENANCE_CATEGORIES.map((c) => ({
                  value: c,
                  label: t(`pms.work.categories.${c}`),
                }))}
                value={categoryFilter || null}
                onChange={(value) => setQuery({ category: value })}
              />
              <Select
                size="xs"
                aria-label={t('pms.work.workOrders.activityFilter')}
                placeholder={t('pms.work.workOrders.activityFilter')}
                searchable
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                data={activities.map((a) => ({
                  value: a,
                  label: t(`pms.work.workTypes.${a}`, { defaultValue: a }),
                }))}
                value={activityFilter || null}
                onChange={(value) => setQuery({ activity: value })}
              />
              <Chip
                size="xs"
                variant="outline"
                checked={alarmOnly}
                onChange={(on) => setQuery({ alarm: on ? '1' : null })}
              >
                {t('pms.work.plan.flags.alarmTest')}
              </Chip>
            </div>
            <WorkOrderPanel
              scope={source.scope}
              items={items}
              canCreate={canCreate}
              pageSize={12}
              showOverdue
              onOpen={(wo) => setQuery({ wo: wo.id })}
              {...(canCreate
                ? { onNew: () => setQuery({ new: '1' }), onGenerate: () => setGenerating(true) }
                : {})}
            />
          </Tabs.Panel>
          {source.defects && vesselId
            ? (['defects', 'breakdowns'] as const).map((key) => (
                <Tabs.Panel key={key} value={key}>
                  <DefectsTab
                    source={source}
                    vesselId={vesselId}
                    kind={key === 'breakdowns' ? 'breakdown' : 'defect'}
                    tree={ready?.tree ?? null}
                    timeZone={timeZone}
                    now={now}
                    query={query}
                    setQuery={setQuery}
                    onChanged={reload}
                  />
                </Tabs.Panel>
              ))
            : null}
        </Tabs>
      )}

      {canCreate && ready ? (
        <>
          <NewWorkOrderDialog
            opened={creating}
            jobs={ready.jobs.filter((j) => j.deletedAt === null && j.active)}
            tree={ready.tree}
            today={utcToShipLocal(now(), timeZone).slice(0, 10)}
            {...(source.work?.assignees ? { assignees: source.work.assignees } : {})}
            onClose={() => setQuery({ new: null })}
            onSubmit={async (input) => {
              const wo = await source.work?.openWorkOrder(input);
              reload();
              setQuery({ new: null, wo: wo?.id ?? null });
            }}
          />
          <GenerateDialog
            opened={generating || generateParam}
            onClose={() => {
              setGenerating(false);
              if (generateParam) setQuery({ generate: null });
            }}
            onSubmit={async (days) => {
              const result = await source.work?.generate(days);
              setGenerating(false);
              if (generateParam) setQuery({ generate: null });
              setNotice(t('pms.work.generate.done', { count: result?.created.length ?? 0 }));
              reload();
            }}
          />
        </>
      ) : null}
    </div>
  );
}

const newWorkOrderSchema = z
  .object({
    mode: z.enum(['planned', 'unplanned']),
    jobId: z.string(),
    kind: z.enum(['unplanned', 'corrective']),
    equipmentId: z.string(),
    title: z.string(),
    priority: z.enum(JOB_PRIORITIES),
    targetOn: z.string(),
    assignedTo: z.string(),
  })
  .superRefine((v, ctx) => {
    const required = (path: string) =>
      ctx.addIssue({ code: 'custom', message: 'REQUIRED', path: [path] });
    if (v.mode === 'planned' && v.jobId === '') required('jobId');
    if (v.mode === 'unplanned') {
      if (v.equipmentId === '') required('equipmentId');
      if (v.title.trim() === '') required('title');
    }
  });
type NewWorkOrderValues = z.infer<typeof newWorkOrderSchema>;

export function toOpenWorkOrder(values: NewWorkOrderValues): OpenWorkOrderInput {
  const assignedTo = values.assignedTo === '' ? null : values.assignedTo;
  return values.mode === 'planned'
    ? { jobId: values.jobId, assignedTo }
    : {
        kind: values.kind,
        equipmentId: values.equipmentId,
        title: values.title.trim(),
        priority: values.priority,
        targetOn: values.targetOn === '' ? null : values.targetOn,
        assignedTo,
      };
}

/**
 * "+ New Work Order" (gemide; PMS belgesi §12): bakım işinden planlı ya da ekipman ve başlıkla
 * plansız / düzeltici iş emri; isteğe bağlı atanan. Numara ve talimat revizyonu sunucudan (K16).
 */
export function NewWorkOrderDialog({
  opened,
  jobs,
  tree,
  today,
  assignees,
  onClose,
  onSubmit,
}: {
  opened: boolean;
  jobs: readonly JobView[];
  tree: EquipmentTree | null;
  today: string;
  assignees?: () => Promise<Assignee[]>;
  onClose: () => void;
  onSubmit: (input: OpenWorkOrderInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.work.newWorkOrder.${key}`);
  const errorText = usePmsErrorText();
  const [error, setError] = useState<unknown>(null);
  const [people, setPeople] = useState<Assignee[]>([]);
  const form = useForm<NewWorkOrderValues>({
    resolver: zodResolver(newWorkOrderSchema),
    defaultValues: {
      mode: 'planned',
      jobId: '',
      kind: 'unplanned',
      equipmentId: '',
      title: '',
      priority: 'medium',
      targetOn: '',
      assignedTo: '',
    },
  });
  useEffect(() => {
    if (!opened) return;
    form.reset();
    setError(null);
    assignees?.()
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [opened, form, assignees]);
  const mode = form.watch('mode');
  const fieldError = (name: keyof NewWorkOrderValues) =>
    form.formState.errors[name]?.message ? t('pms.fieldErrors.REQUIRED') : undefined;
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit(toOpenWorkOrder(values));
    } catch (failure) {
      setError(failure);
    }
  });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('title')}
      size="lg"
      closeButtonProps={{ 'aria-label': t('pms.work.password.back') }}
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <Controller
            control={form.control}
            name="mode"
            render={({ field }) => (
              <SegmentedControl
                fullWidth
                aria-label={k('mode')}
                data={[
                  { value: 'planned', label: k('fromJob') },
                  { value: 'unplanned', label: k('unplanned') },
                ]}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {mode === 'planned' ? (
            <Controller
              control={form.control}
              name="jobId"
              render={({ field }) => (
                <Select
                  label={k('job')}
                  required
                  searchable
                  data={jobs.map((j) => ({ value: j.id, label: `${j.code} · ${j.title}` }))}
                  value={field.value || null}
                  onChange={(value) => field.onChange(value ?? '')}
                  error={fieldError('jobId')}
                />
              )}
            />
          ) : (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Controller
                  control={form.control}
                  name="equipmentId"
                  render={({ field }) => (
                    <Select
                      label={k('equipment')}
                      required
                      searchable
                      data={(tree?.nodes ?? []).map((n) => ({
                        value: n.id,
                        label: `${n.name} · ${n.code}`,
                      }))}
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                      error={fieldError('equipmentId')}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="kind"
                  render={({ field }) => (
                    <Select
                      label={k('kind')}
                      data={['unplanned', 'corrective'].map((v) => ({
                        value: v,
                        label: t(`pms.work.detail.kinds.${v}`),
                      }))}
                      value={field.value}
                      allowDeselect={false}
                      onChange={(value) => field.onChange(value ?? 'unplanned')}
                    />
                  )}
                />
              </SimpleGrid>
              <TextInput
                label={k('task')}
                required
                {...form.register('title')}
                error={fieldError('title')}
              />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Controller
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <Select
                      label={k('priority')}
                      data={JOB_PRIORITIES.map((p) => ({
                        value: p,
                        label: t(`pms.work.priority.${p}`),
                      }))}
                      value={field.value}
                      allowDeselect={false}
                      onChange={(value) => field.onChange(value ?? 'medium')}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="targetOn"
                  render={({ field }) => (
                    <DayInput
                      label={k('targetOn')}
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                      minDate={today}
                      clearable
                      clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                    />
                  )}
                />
              </SimpleGrid>
            </>
          )}
          {people.length > 0 ? (
            <Controller
              control={form.control}
              name="assignedTo"
              render={({ field }) => (
                <Select
                  label={k('assignedTo')}
                  clearable
                  clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                  data={people.map((p) => ({
                    value: p.id,
                    label: p.role ? `${p.name} · ${p.role}` : p.name,
                  }))}
                  value={field.value || null}
                  onChange={(value) => field.onChange(value ?? '')}
                />
              )}
            />
          ) : null}
          {error ? (
            <p className={work.error} role="alert">
              {errorText(error)}
            </p>
          ) : null}
          <div className={work.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {t('pms.work.password.back')}
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {k('submit')}
            </Button>
          </div>
        </Stack>
      </form>
    </Modal>
  );
}

/** "Generate Work Orders" (K9): ufuktaki ve gecikmiş işlere birer iş emri; kopya açılmaz. */
export function GenerateDialog({
  opened,
  onClose,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  onSubmit: (days: number) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [days, setDays] = useState<number | ''>(DEFAULT_GENERATE_HORIZON_DAYS);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('pms.work.generate.title')}
      closeButtonProps={{ 'aria-label': t('pms.work.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (days === '') return;
          setBusy(true);
          setError(null);
          try {
            await onSubmit(days);
          } catch (failure) {
            setError(failure);
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className={work.dialogBody}>{t('pms.work.generate.body', { days: days || 0 })}</p>
        <NumberInput
          label={t('pms.work.generate.horizon')}
          min={1}
          max={365}
          decimalScale={0}
          value={days}
          onChange={(value) => setDays(typeof value === 'number' ? value : '')}
        />
        {error ? (
          <p className={work.error} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('pms.work.password.back')}
          </Button>
          <Button type="submit" loading={busy} disabled={days === ''}>
            {t('pms.work.generate.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
